import type { SequenceStep } from '~/components/Sequence/sequenceSteps';

/**
 * Horizontal layout for the sequence canvas.
 *
 * Every step's node is centered on the spine (x = `cx`). A node can be narrow (an icon tile) or wide (a
 * wait pill with the days/weeks toggle, an await pill, a group button-box), so a fixed offset for the
 * side branches (a spawn's "runs alongside" box, an await's not-accepted arm) inevitably overlaps the
 * widest node. Instead each side branch gets its OWN vertical LANE to the right of the WIDEST node, and
 * lanes are packed left-to-right in step order — so nothing overlaps however the steps change.
 */

/** Gap between the widest node's right edge and the first branch lane. */
const LANE_GAP = 48;
/**
 * A not-accepted arm draws its nodes CENTERED on the lane's x (unlike a spawn's left-aligned box), so the
 * lane must leave room for the widest node that can sit there (a wait pill) to not poke back into the spine.
 */
const LANE_NODE_HALF = 110;
/** Width reserved per branch lane (a spawn box / not-accepted column + breathing room). */
export const LANE_W = 320;

/** Estimated half-width (px) of a node centered on the spine — drives where branches can safely sit. */
export function spineNodeHalfWidth(step: SequenceStep): number {
  if (step.type === 'wait') return 110; // clock + number + days/weeks segment
  if (step.type === 'await_connect') return 75; // clock + number + "days"
  if (step.type === 'switch') return 82; // dataflow icon + Age/~AUM segment (a wait-pill-like node)
  if (step.type === 'group') {
    const chips = step.arms?.filter((a) => a.length > 0).length ?? 0;
    return (16 + (chips + 1) * 44) / 2; // the inline button-box: padding + chips + "+"
  }
  return 20; // a plain icon tile (~36px)
}

/** Whether a step draws a side branch that needs its own right-hand lane: a spawn (always) or an await
 * WITH an arm. A switch does NOT — it's terminal and fans its route branches DOWN below the node (see
 * SequenceSwitchBranches), so it needs no lane to the right. */
export function stepHasBranchLane(step: SequenceStep): boolean {
  if (step.type === 'spawn') return true;
  return step.type === 'await_connect' && (step.branches?.notAccepted?.length ?? 0) > 0;
}

export interface BranchLanes {
  /** Half-width of the widest node on the spine (so branches clear even the widest one). */
  maxSpineHalfW: number;
  /** X of the first branch lane (right of the widest node). */
  laneX0: number;
  /** Number of lanes in use. */
  laneCount: number;
  /** The lane index (0-based) assigned to step `i`, or null if it has no branch. */
  laneOf: (i: number) => number | null;
  /** X (left edge) of step `i`'s branch lane. Falls back to `laneX0` for a step with no lane. */
  branchXFor: (i: number) => number;
  /** X where the "if not accepted" STUB button (an await with no arm yet) sits — just past its own node. */
  stubXFor: (i: number) => number;
  /** Center X of the connector run between node `i`'s right edge and its lane (for the branch's label). */
  labelXFor: (i: number) => number;
  /** Right edge of the whole canvas content (for width reservation). */
  contentRight: (cx: number) => number;
}

/** Compute the branch-lane layout for a spine of `steps` centered at `cx`. Pure + recomputed per render. */
export function computeBranchLanes(steps: SequenceStep[], cx: number): BranchLanes {
  const maxSpineHalfW = steps.reduce((m, s) => Math.max(m, spineNodeHalfWidth(s)), 20);
  // Leave room for the spine's widest node AND a lane's widest (centered) node, so neither pokes into the other.
  const laneX0 = cx + maxSpineHalfW + LANE_GAP + LANE_NODE_HALF;

  // Assign the INNERMOST lane (0) to the LAST branch and work outward. A branch's connector runs from the
  // spine across every INNER lane at that branch's OWN y; putting later (lower) branches in the inner lanes
  // means an earlier branch's long connector only crosses lanes whose (later) branches start BELOW it — i.e.
  // empty at that y — so no connector ever crosses another branch's nodes.
  const lane = new Map<number, number>();
  const branchSteps = steps.map((s, i) => (stepHasBranchLane(s) ? i : -1)).filter((i) => i >= 0);
  [...branchSteps].reverse().forEach((i, laneIdx) => lane.set(i, laneIdx));

  const nodeRight = (i: number) => cx + spineNodeHalfWidth(steps[i] ?? { type: 'follow' });
  const laneOf = (i: number) => lane.get(i) ?? null;
  const branchXFor = (i: number) => laneX0 + (lane.get(i) ?? 0) * LANE_W;
  const stubXFor = (i: number) => nodeRight(i) + 24;
  // The label sits on the connector, between the node's right edge and the lane's LEFT (node) edge.
  const labelXFor = (i: number) => (nodeRight(i) + branchXFor(i) - LANE_NODE_HALF) / 2;

  return {
    maxSpineHalfW,
    laneX0,
    laneCount: lane.size,
    laneOf,
    branchXFor,
    stubXFor,
    labelXFor,
    contentRight: (c: number) => (lane.size === 0 ? c + maxSpineHalfW + 40 : laneX0 + lane.size * LANE_W),
  };
}
